import { Close, Folder, Search } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { PublicFolderShare } from "../../../types";
import { hostname } from "../../../utils";

interface PublicFolderSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (folder: PublicFolderShare) => void;
  excludedFolderIds: string[];
  getToken: () => Promise<string>;
}

const PublicFolderSelector: React.FC<PublicFolderSelectorProps> = ({
  open,
  onClose,
  onSelect,
  excludedFolderIds,
  getToken,
}) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<PublicFolderShare[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch public folders
  useEffect(() => {
    if (!open) return;

    const fetchFolders = async () => {
      setLoading(true);
      const currentToken = await getToken();

      try {
        const params = new URLSearchParams();
        params.append("per_page", "50");
        if (debouncedSearch) {
          params.append("search", debouncedSearch);
        }

        const response = await axios.get(
          `${hostname}/folder-shares/public?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        const allFolders: PublicFolderShare[] = response.data.shares || [];
        // Filter out already attached folders
        const availableFolders = allFolders.filter(
          (f) => !excludedFolderIds.includes(f.id)
        );
        setFolders(availableFolders);
      } catch (err: any) {
        console.error("Failed to fetch public folders:", err);
        setFolders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [open, debouncedSearch, excludedFolderIds, getToken]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Select Public Folder
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search public folders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : folders.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            {debouncedSearch
              ? "No public folders found matching your search."
              : "No public folders available."}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {folders.map((folder) => (
              <ListItemButton
                key={folder.id}
                onClick={() => onSelect(folder)}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemIcon>
                  <Folder color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={folder.folderName || folder.folderPath}
                  secondary={
                    <>
                      {folder.ownerCompany && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          From: {folder.ownerCompany.name}
                        </Typography>
                      )}
                      {folder.description && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {folder.description}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublicFolderSelector;
